import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSectorTerms } from '@/utils/useSectorTerms';

export const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    due_date: '',
    recurrence: 'none',
    assigned_users: [],
  });
  const { user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  // Get labels from current user (admin) — payment/identifier still come from
  // BUSINESS_TYPE_LABELS (backend), people-related strings come from terms.
  const labels = currentUser?.labels || { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };
  const terms = useSectorTerms();

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, []);

  const fetchPayments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/payments`, { withCredentials: true });
      setPayments(data);
    } catch (error) {
      toast.error('Ödemeler yüklenemedi');
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data.filter((u) => u.role === 'user'));
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
      };
      await axios.post(`${API_URL}/api/payments`, payload, { withCredentials: true });
      toast.success('Ödeme oluşturuldu');
      setDialogOpen(false);
      setFormData({ title: '', description: '', amount: '', due_date: '', recurrence: 'none', assigned_users: [] });
      fetchPayments();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Ödemeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${paymentId}`, { withCredentials: true });
      toast.success('Ödeme silindi');
      fetchPayments();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter((id) => id !== userId)
        : [...prev.assigned_users, userId],
    }));
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="payments-page-title">
                {labels.payment}
              </h1>
              <p className="text-base text-[#595959] mt-2">{labels.payment} tanımları ve yönetimi</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                  onClick={() => setFormData({ title: '', description: '', amount: '', due_date: '', recurrence: 'none', assigned_users: [] })}
                  data-testid="add-payment-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {labels.payment} Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="payment-dialog">
                <DialogHeader>
                  <DialogTitle>Yeni {labels.payment}</DialogTitle>
                  <DialogDescription>{labels.payment} bilgilerini girin</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Başlık</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="payment-title-input"
                    />
                  </div>
                  <div>
                    <Label>Açıklama</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      data-testid="payment-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tutar (TL)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Son Ödeme Tarihi</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        required
                        data-testid="payment-duedate-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Tekrarlama</Label>
                    <Select value={formData.recurrence} onValueChange={(value) => setFormData({ ...formData, recurrence: value })}>
                      <SelectTrigger data-testid="payment-recurrence-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Tek Seferlik</SelectItem>
                        <SelectItem value="weekly">Haftalık</SelectItem>
                        <SelectItem value="monthly">Aylık</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Atanan {terms.customerLabelPlural}</Label>
                    <div className="border border-[#E5E5E5] rounded-lg p-4 max-h-48 overflow-y-auto space-y-2" data-testid="user-selection-list">
                      {users.map((user) => (
                        <div key={user.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={formData.assigned_users.includes(user.id)}
                            onCheckedChange={() => toggleUserSelection(user.id)}
                            data-testid={`user-checkbox-${user.id}`}
                          />
                          <span className="text-sm">{user.name} - {user.apartment_number || `${terms.customerNoLabel} yok`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="payment-submit-button">
                    Oluştur
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl hidden md:block" data-testid="payments-table-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Tutar</TableHead>
                  <TableHead>Son Tarih</TableHead>
                  <TableHead>Tekrar</TableHead>
                  <TableHead>Atanan</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <TableCell className="font-medium">{payment.title}</TableCell>
                    <TableCell>{payment.amount} ₺</TableCell>
                    <TableCell>{new Date(payment.due_date).toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell>
                      {payment.recurrence === 'none' ? 'Tek' : payment.recurrence === 'monthly' ? 'Aylık' : 'Haftalık'}
                    </TableCell>
                    <TableCell>{payment.assigned_users.length} kişi</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(payment.id)}
                        data-testid={`delete-payment-${payment.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#DC2626]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="payments-mobile-cards">
            {payments.map((payment) => (
              <div key={payment.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid={`payment-card-${payment.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] truncate">{payment.title}</p>
                    <p className="text-lg font-bold text-[#2563EB] mt-0.5">{payment.amount} ₺</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(payment.id)} className="shrink-0">
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#595959]">
                  <span>📅 {new Date(payment.due_date).toLocaleDateString('tr-TR')}</span>
                  <span>🔁 {payment.recurrence === 'none' ? 'Tek' : payment.recurrence === 'monthly' ? 'Aylık' : 'Haftalık'}</span>
                  <span>👥 {payment.assigned_users.length} kişi</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      
    </PageLayout>
  );
};
